/**
 * Qualidade do dado (plano 5.8): dado ruim aparece, não some. Cada item é
 * `{codigo, nivel, rotulo, quantidade, itens}`, com `itens` = linhas do
 * extrato (a tela abre o painel do ambiente a partir delas).
 *
 * Duas guardas acinzentam a ocupação: críticos em mais de 10% da fila
 * ("incompleto") e planilha sem lançamento há mais de 3 dias úteis.
 */
import { F } from "../gerencialFields";
import { indiceDeIdentidade } from "../identidadeAmbiente";
import { normalizeText } from "../data";
import { duEntre } from "./calendario";
import { CAMADA, SINAL, SITUACAO } from "./classificacao";
import { AJUSTE_CARREGAMENTO_SEM_TERMINO, AJUSTE_TERMINO_APOS_CARREGAMENTO, possivelServico } from "./normalizacao";
import { percentil } from "./precisao";
import { intervaloProvisorio, janelaDoRitmo } from "./ritmo";

export const NIVEL = Object.freeze({ CRITICO: "critico", AVISO: "aviso", INFO: "info" });

export const LIMITE_INCOMPLETO = 0.1;
export const DU_PLANILHA_PARADA = 3;
// Com menos durações que isso o p90 seria um ou dois ambientes, não "o normal".
const AMOSTRA_MINIMA_PARADO = 5;

const SINAIS_CRITICOS_DA_FILA = [SINAL.ERRO_FORMULA_ENTREGA, SINAL.TERMINO_FUTURO];
const ORDEM_NIVEL = [NIVEL.CRITICO, NIVEL.AVISO, NIVEL.INFO];

function item(codigo, nivel, rotulo, itens, quantidade = itens.length) {
  return { codigo, nivel, rotulo, quantidade, itens };
}

function comSinal(linhas, sinal) {
  return linhas.filter((linha) => linha.sinais.includes(sinal));
}

function daFabrica(linha) {
  return linha.situacao === SITUACAO.ABERTO || linha.situacao === SITUACAO.TERMINADO;
}

function paradosEmProducao(linhas, naoUteis, hoje) {
  const janela = janelaDoRitmo(hoje);
  const duracoes = linhas
    .filter(
      (l) =>
        l.situacao === SITUACAO.TERMINADO &&
        l.marcenaria_inicio &&
        l.marcenaria_inicio <= l.termino &&
        l.termino >= janela.de &&
        l.termino <= janela.ate,
    )
    .map((l) => duEntre(l.marcenaria_inicio, l.termino, naoUteis));
  if (duracoes.length < AMOSTRA_MINIMA_PARADO) return { p90: null, itens: [] };
  const p90 = percentil(duracoes, 0.9);
  const itens = linhas.filter(
    (l) =>
      l.situacao === SITUACAO.ABERTO &&
      l.camada === CAMADA.EM_PRODUCAO &&
      l.marcenaria_inicio <= hoje &&
      duEntre(l.marcenaria_inicio, hoje, naoUteis) > p90,
  );
  return { p90, itens };
}

function pedidosComVariosClientes(linhas) {
  const comoPlanilha = linhas.map((l) => ({
    [F.NUMERO_DO_PEDIDO]: l.pedido,
    [F.NOME_DO_CLIENTE]: l.cliente,
    [F.AMBIENTE]: l.ambiente,
  }));
  const { pedidosComVariosClientes: pedidos } = indiceDeIdentidade(comoPlanilha);
  const ambiguos = new Set(pedidos.map((p) => p.pedidoNormalizado));
  return { quantidade: pedidos.length, itens: linhas.filter((l) => ambiguos.has(normalizeText(l.pedido))) };
}

function formatarDeriva(deriva) {
  const pct = Math.round(deriva * 100);
  return `${pct > 0 ? "+" : ""}${pct}%`;
}

/**
 * Maior data realizada ≤ hoje (corte, término, carregamento) e há quantos
 * du ela foi. A planilha "parada" há mais de 3 du deixa a fila velha.
 */
export function planilhaParada(linhas, naoUteis, hoje) {
  let dataEfetiva = null;
  (linhas || []).forEach((l) => {
    [l.corte, l.marcenaria_termino, l.termino, l.carregamento].forEach((data) => {
      if (data && data <= hoje && (!dataEfetiva || data > dataEfetiva)) dataEfetiva = data;
    });
  });
  if (!dataEfetiva) return { desde: null, du: null };
  const du = duEntre(dataEfetiva, hoje, naoUteis);
  return du > DU_PLANILHA_PARADA ? { desde: dataEfetiva, du } : null;
}

function itensDeQualidade({ extrato, classificacao, ritmo, naoUteis, hoje }) {
  const linhas = classificacao.linhas;
  const abertos = linhas.filter((l) => l.situacao === SITUACAO.ABERTO);
  const terminados = linhas.filter((l) => l.situacao === SITUACAO.TERMINADO);
  const parados = paradosEmProducao(linhas, naoUteis, hoje);
  const variosClientes = pedidosComVariosClientes(linhas);
  const provisorio = intervaloProvisorio(hoje);
  const duplicadas = classificacao.duplicadas || [];
  return [
    item(SINAL.ERRO_FORMULA_ENTREGA, NIVEL.CRITICO, "Data reprogramada com erro de fórmula (#VALUE!, #N/A…)", comSinal(abertos, SINAL.ERRO_FORMULA_ENTREGA)),
    item(SINAL.TERMINO_FUTURO, NIVEL.CRITICO, "Término lançado com data futura (contado como aberto)", comSinal(abertos, SINAL.TERMINO_FUTURO)),
    item(SINAL.TERMINO_ANTES_INICIO, NIVEL.CRITICO, "Término antes do início da marcenaria", comSinal(terminados, SINAL.TERMINO_ANTES_INICIO)),
    item(SINAL.SEM_DATA_ENTREGA, NIVEL.AVISO, "Aberto sem data de entrega (vai para o fim da fila)", comSinal(abertos, SINAL.SEM_DATA_ENTREGA)),
    item("chave_duplicada", NIVEL.AVISO, "Ambiente repetido no mesmo pedido (vale a última linha)", duplicadas.flatMap((d) => d.linhas), duplicadas.length),
    item("ajuste_termino_apos_carregamento", NIVEL.AVISO, "Término lançado depois do carregamento (vale o carregamento)", linhas.filter((l) => daFabrica(l) && l.ajuste_termino === AJUSTE_TERMINO_APOS_CARREGAMENTO)),
    item("ajuste_carregamento_sem_termino", NIVEL.AVISO, "Carregado sem término lançado (vale o carregamento)", linhas.filter((l) => daFabrica(l) && l.ajuste_termino === AJUSTE_CARREGAMENTO_SEM_TERMINO)),
    item("parado_em_producao", NIVEL.AVISO, `Em produção há mais de ${Math.round(parados.p90 || 0)} dias úteis (acima de 9 em cada 10 ambientes)`, parados.itens),
    item("deriva_ritmo", NIVEL.AVISO, `Ritmo das últimas 8 semanas ${ritmo?.avisoDeriva ? formatarDeriva(ritmo.deriva) : ""} em relação às 26 semanas`, [], ritmo?.avisoDeriva ? 1 : 0),
    item("pedido_com_varios_clientes", NIVEL.AVISO, "Mesmo número de pedido para clientes diferentes", variosClientes.itens, variosClientes.quantidade),
    item("sem_chave", NIVEL.AVISO, "Linha sem número do pedido ou sem ambiente", linhas.filter((l) => !l.chave)),
    item("possivel_servico", NIVEL.AVISO, "Nome cita serviço ou troca: conta como ambiente; se for serviço, marque o lote como SERV.", abertos.filter(possivelServico)),
    item("termino_provisorio", NIVEL.INFO, "Términos das últimas 2 semanas (ainda podem chegar mais)", terminados.filter((l) => l.termino >= provisorio.de)),
    item("data_texto", NIVEL.INFO, "Data digitada como texto não reconhecido", linhas.filter((l) => (l.erros_texto || []).length > 0)),
    // Não muda a fila; muda o "o que digitar" do Cenário do PCP, que precisa
    // dos feriados que o Excel usa no WORKDAY de CO e CR.
    item("aba_tabelas_ausente", NIVEL.AVISO, "Aba TABELAS não encontrada: o \"o que digitar\" do Cenário do PCP conta só sábado e domingo como dia sem expediente", [], extrato?.tabelasAusente === true ? 1 : 0),
  ];
}

/**
 * `avaliarQualidade({extrato, classificacao, ritmo, fila, naoUteis, hoje})` →
 * `{itens, incompleto, percentualCritico, planilhaParada}`.
 * - `classificacao`: retorno de `classificarLinhas`.
 * - `ritmo`: retorno de `calcularRitmo` (deriva).
 * - `fila`: retorno de `projetarFila` (base da guarda de 10%).
 */
export function avaliarQualidade({ extrato, classificacao, ritmo, fila, naoUteis, hoje } = {}) {
  if (!extrato?.ok) {
    const faltando = extrato?.faltando || [];
    const rotulo = faltando.length
      ? `Layout da planilha não reconhecido: falta ${faltando.join(", ")}`
      : "Layout da planilha não reconhecido";
    return {
      itens: [{ ...item("layout", NIVEL.CRITICO, rotulo, []), quantidade: 1, faltando }],
      incompleto: true,
      percentualCritico: 100,
      planilhaParada: null,
    };
  }
  const itens = itensDeQualidade({ extrato, classificacao, ritmo, naoUteis, hoje })
    .filter((i) => i.quantidade > 0)
    .sort((a, b) => ORDEM_NIVEL.indexOf(a.nivel) - ORDEM_NIVEL.indexOf(b.nivel));
  const naFila = fila?.itens || [];
  const criticosNaFila = naFila.filter((i) => i.sinais.some((s) => SINAIS_CRITICOS_DA_FILA.includes(s))).length;
  const proporcao = naFila.length > 0 ? criticosNaFila / naFila.length : 0;
  return {
    itens,
    incompleto: proporcao > LIMITE_INCOMPLETO,
    percentualCritico: Math.round(proporcao * 100),
    planilhaParada: planilhaParada(classificacao.linhas, naoUteis, hoje),
  };
}
